import { postJSON, fetch } from '@catalogo/core-http';
import { Device } from '@catalogo/core-queries/device.graphql';
import { getIn } from 'seamless-immutable';
import { getCurrentSku } from '@catalogo/core-sku';
import { getPageType } from '@catalogo/core-connect/page-type';
import { readCookies } from '@catalogo/core-connect/cookies';
import { readConfig } from '@catalogo/core-connect/config';
import { getProductShop } from './queries/product-shop.graphql';
import { getProduct } from './queries/product.graphql';

export const fetchMetrics = (url, data) => {
  if ('sendBeacon' in navigator) {
    window.addEventListener('unload', event => {
      navigator.sendBeacon(url, data);
    });
  } else if (data) {
    postJSON(url, data);
  } else {
    fetch(url);
  }
};

export const metricsLogger = (detail, event, body) => {
  if (window.location.search.indexOf('log=true') >= 0) {
    console.info(detail, event, body);
  }
};

export const dispatchEvent = (event, body) => {
  const eventBody = { detail: body };
  metricsLogger('catalogo-core:', event, body);
  window.document.dispatchEvent(new window.CustomEvent(event, eventBody));
};

export const createNavigation = () => ({
  url: window.location.href,
  host: window.location.host,
  referrer: window.document.referrer,
  pathname: window.document.location.pathname,
});

export const createPage = ({ routerProps, client, body }) => {
  try {
    const type = getPageType(routerProps?.location?.pathname);
    const { cookies } = readCookies(client);
    const page = getIn(body, ['page']) || {};
    const spaceyComponents = getIn(page, ['components']) || {};
    const components = type === 'product' ? ['page', 'recommendation', 'b2wAds', 'freight', 'sku'] : undefined;
    const breadcrumb = page.breadcrumb || [];
    const slug = routerProps?.params?.slug;

    return {
      slug,
      type,
      title: page.title,
      description: page.description,
      breadCrumb: [...breadcrumb].reverse(),
      canonical: page.canonical,
      b2wSid: cookies['B2W-SID'],
      b2wuid: cookies['B2W-UID'],
      channel: cookies.b2wChannel,
      epar: cookies.b2wEPar,
      franq: cookies.b2wFranq,
      opn: cookies.b2wOpn,
      chave: cookies.b2wChave,
      components,
      spaceyComponents,
    };
  } catch {
    return null;
  }
};

export const createType = createPage;

export const createDevice = ({ client }) => {
  const data = client.readQuery({ query: Device }) || {};
  return {
    userAgent: window.navigator.userAgent,
    lang: window.navigator.userLanguage || window.navigator.language,
    width: window.screen.width,
    height: window.screen.height,
    platform: window.navigator.platform,
    type: data.device,
  };
};

export const createProject = ({ client }) => {
  try {
    const { config } = readConfig(client);

    return {
      brand: config.brand,
      domain: window.location.host,
      name: `catalogo-v3-${config.brand}`,
      side: 'client',
      version: config.version,
    };
  } catch {
    return null;
  }
};

export const createContext = ({ client }) => {
  try {
    const { cookies } = readCookies(client);

    return {
      c_cep: cookies.persistentCep,
      c_b2wChannel: cookies.b2wChannel,
      c_b2wChave: cookies.b2wChave,
      c_b2wDeviceType: cookies.b2wDeviceType,
      c_b2wOpn: cookies.b2wOpn,
      c_b2wPid: cookies['B2W-PID'],
      c_b2wSid: cookies['B2W-SID'],
      c_b2wUid: cookies['B2W-UID'],
      c_opn: cookies.b2wOpn,
      c_region: cookies.b2wRegion,
      c_customerId: cookies['customer.id'],
      c_b2wFranq: cookies.b2wFranq,
      c_b2wEPar: cookies.b2wEPar,
      c_salesSolution: cookies.salesSolution,
      c_prime: cookies.c_prime,
      c_finance: cookies.c_finance,
    };
  } catch {
    return null;
  }
};

const getAdsProducts = search => {
  const { products } = search;
  if (!products) {
    return null;
  }
  const productAds = [];
  products.map(item => {
    const { product } = item;
    if (product?.type === 'ads') {
      productAds.push(product.id);
    }
  });
  return productAds;
};

export const createSearch = ({ body }) => {
  try {
    if (!body?.search) {
      return null;
    }
    const search = Object.assign({}, body?.search);
    const products = body?.search?.products || [];

    const categories = search?.aggregations?.[0]?.options;
    search.categories = categories;
    search.adsProducts = getAdsProducts(search);
    search.products = search.productIds;
    search.productList = products.map(product => product?.product || product);
    search.results = search.total;

    return search;
  } catch {
    return null;
  }
};

export const createVariations = ({ routerProps, client, body }) => {
  try {
    const { parsedQs } = routerProps;
    const { product } = body;
    if (!product) {
      return null;
    }
    const { diffs } = product;
    return diffs ? diffs.reduce((obj, { type }) => ({ ...obj, [type]: parsedQs[type.toLowerCase()] }), {}) : null;
  } catch {
    return null;
  }
};

export const createProduct = ({ routerProps, client, body }) => {
  try {
    const { parsedQs } = routerProps;
    const { product } = body;
    if (!product) {
      return null;
    }
    const sku = getCurrentSku(body, parsedQs);
    const newProduct = {
      ...product,
      attributes: { id: product.id, title: 'Informações Técnicas', properties: getIn(product, ['attributes'], []) },
      _embedded: {
        seller: {
          ...getIn(sku, ['offers', 'result', 0, 'seller'], {}),
        },
      },
    };

    return newProduct;
  } catch {
    return null;
  }
};

export const createUser = ({ client }) => {
  try {
    const { cookies } = readCookies(client);

    return {
      'customer.id': cookies['customer.id'],
      'B2W-SID': cookies['B2W-SID'],
      'B2W-UID': cookies['B2W-UID'],
      'B2W-PID': cookies['B2W-PID'],
      b2wEPar: cookies['b2wEPar'],
      searchTestAB: cookies['searchTestAB'],
    };
  } catch {
    return null;
  }
};

export const createOffers = ({ client, body }) => {
  try {
    const { config } = readConfig(client);
    const skus = getIn(body, ['product', 'skus'], []);
    const offers = skus.reduce((skuOffer = [], { offers }) => (offers ? skuOffer.concat(offers.result) : skuOffer), []);
    return offers.map(offer => ({
      ...offer,
      ['_embedded']: { seller: offer?.seller },
      ['_links']: { sku: { id: offer?.sku } },
      stamps: {
        freight: getIn(offer, ['stamps', 'prime']) ? ['PRIME'] : [],
      },
      paymentOptions: {
        BOLETO: offer?.boleto,
        CARTAO_VISA: offer?.cartaoVisa,
        [config.brandCardId]: offer?.cartaoMarca,
      },
    }));
  } catch {
    return null;
  }
};

const getProductInfo = (variables, client, brand) => {
  const data = client.readQuery({ query: brand === 'shop' ? getProductShop : getProduct, variables });
  return data;
};

export const createMainOffer = ({ routerProps, client, body }) => {
  try {
    const { params, parsedQs } = routerProps;
    const { config } = readConfig(client);
    const { brand, brandCardId } = config;
    const variables = {
      productId: params?.id,
      offerId: parsedQs['offerId'] || '',
      sellerId: parsedQs['sellerId'] || '',
      brandCardId: brandCardId,
      hasOfferId: !!parsedQs.offerId,
    };
    const data = getProductInfo(variables, client, brand);

    if (!data) {
      return null;
    }

    const sku = getCurrentSku(data, parsedQs);

    const bestOffer = getIn(sku, ['offers', 'result'], []).map(offer => ({
      ...offer,
      group: offer.group?.map(({ id, embedded }) => ({ id, _embedded: embedded })),
      ['_embedded']: { seller: offer.seller },
      ['_links']: { sku: { id: offer.sku } },
      stamps: {
        freight: getIn(offer, ['stamps', 'prime']) ? ['PRIME'] : [],
      },
      paymentOptions: {
        BOLETO: offer.boleto,
        CARTAO_VISA: offer.cartaoVisa,
        [brandCardId]: offer.cartaoMarca,
      },
    }))[0];

    return bestOffer;
  } catch (err) {
    return null;
  }
};

export const createEventID = () => {
  try {
    return `${new Date().getTime()}.${Math.random().toString()}`;
  } catch (error) {
    return null;
  }
};

export const createHasUsed = ({ body }) => {
  const currentSku = getCurrentSku(body);
  return getIn(currentSku, ['offers', 'result', 0, 'condition']) === 'USED';
};

export const createBuyboxToken = ({ routerProps, body }) => {
  if (!body || !body.product) {
    return null;
  }

  const { parsedQs } = routerProps;
  const sku = getCurrentSku(body, parsedQs);
  const buyboxToken = sku?.offers?.meta?.buyboxToken;

  return buyboxToken;
};

export const createServiceTokens = ({ routerProps, body }) => {
  try {
    const { parsedQs } = routerProps;
    const currentSku = getCurrentSku(body, parsedQs);
    const buyboxToken = getIn(currentSku, ['offers', 'meta', 'buyboxToken']);
    return { buyboxToken };
  } catch {
    return null;
  }
};

export const createPickUpStoreOffers = ({ body }) => {
  try {
    const offers = body?.product?.lasa?.result || [];

    return offers.map(offer => ({
      ...offer,
      ['_embedded']: { seller: offer.seller },
      ['_links']: { sku: { id: offer.sku } },
      stamps: {
        freight: offer?.stamps?.prime ? ['PRIME'] : [],
      },
      paymentOptions: {
        BOLETO: offer.boleto,
        CARTAO_VISA: offer.cartaoVisa,
        CARTAO_ACOM_MASTERCARD: offer.cartaoMarca,
      },
    }));
  } catch {
    return null;
  }
};
